import type { RecordingAssessmentFeedbackStatus } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { nestedControlsStyle } from "@repo/ui/nestedControls";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconArchive, IconCheck, IconCircle } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";

interface FeedbackStatusAction {
  label: string;
  status: RecordingAssessmentFeedbackStatus;
  icon: ReactNode;
}

/** The product policy for transitions shown from each current status. */
export const feedbackStatusActions: Record<
  RecordingAssessmentFeedbackStatus,
  readonly FeedbackStatusAction[]
> = {
  open: [
    {
      label: "Resolve",
      status: "resolved",
      icon: <IconCheck aria-hidden className="size-4" />,
    },
    {
      label: "Mark as outdated",
      status: "outdated",
      icon: <IconArchive aria-hidden className="size-4" />,
    },
  ],
  resolved: [
    {
      label: "Open",
      status: "open",
      icon: <IconCircle aria-hidden className="size-4" />,
    },
    {
      label: "Mark as outdated",
      status: "outdated",
      icon: <IconArchive aria-hidden className="size-4" />,
    },
  ],
  outdated: [
    {
      label: "Open",
      status: "open",
      icon: <IconCircle aria-hidden className="size-4" />,
    },
    {
      label: "Resolve",
      status: "resolved",
      icon: <IconCheck aria-hidden className="size-4" />,
    },
  ],
};

export interface FeedbackReviewActionsProps {
  status: RecordingAssessmentFeedbackStatus;
  onStatusChange: (status: RecordingAssessmentFeedbackStatus) => void;
}

/** Icon actions that appear over the right edge of a hovered feedback row. */
export const FeedbackReviewActions: FC<FeedbackReviewActionsProps> = (
  props,
) => {
  const { status, onStatusChange } = props;
  const actions = feedbackStatusActions[status];

  if (actions.length === 0) {
    return null;
  }

  return (
    <div
      className={nestedControlsStyle.secondaryControl({
        className:
          "pointer-events-none absolute top-4 right-4 flex gap-1 rounded-lg border border-border-light bg-white p-1 opacity-0 shadow-md transition-opacity duration-150 group-[:focus-within]/row:pointer-events-auto group-[:focus-within]/row:opacity-100 group-[:hover]/row:pointer-events-auto group-[:hover]/row:opacity-100 motion-reduce:transition-none",
      })}
    >
      {actions.map((action) => {
        return (
          <TooltipTrigger key={action.status}>
            <IconButton
              accessibleLabel={action.label}
              className="size-6"
              icon={action.icon}
              onClick={() => onStatusChange(action.status)}
              size={Size.SMALL}
              variant={ButtonVariant.Tertiary}
            />
            <SimpleTooltip>{action.label}</SimpleTooltip>
          </TooltipTrigger>
        );
      })}
    </div>
  );
};
